"use client";

import React from "react";
import { Briefcase, MessageCircle, ShieldCheck, Target, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const pillars = [{
  title: "Expertise",
  description: "Je \xA0maîtrise les cadres réglementaires de la formation professionnelle, de France Compétences, du Référentiel National Qualité. En tant qu’évaluatrice technique pour Cofrac, je suis également au cœur des évolutions normatives et des pratiques d’excellence.",
  icon: Briefcase
}, {
  title: "Accessibilité",
  description: "Les textes règlementaires sont souvent complexes et techniques. Mon rôle est de les décrypter, de les traduire en langage clair et de les rendre accessibles à tous vos collaborateurs, quelle que soit leur niveau initial. L’objectif : que chacun comprenne les enjeux et s’approprie les bonnes pratiques.",
  icon: MessageCircle
}, {
  title: "Co-construction systématique",
  description: "Aucun accompagnement ne peut réussir sans l’implication de vos équipes. Je privilégie la co-construction à chaque étape : votre expertise métier, vos pratiques terrain et votre connaissance de vos publics sont indispensables. Je structure, formalise et sécurise ce qui existe déjà chez vous.",
  icon: Users
}, {
  title: "Éthique",
  description: "Effet Conseil intègre une dimension RSE dans tous ses accompagnements. Référente RSE certifiée, j’applique des principes éthiques dans mes pratiques : respect des personnes, transparence, équité, amélioration continue et responsabilité environnementale. Ces valeurs irriguent la manière dont nous travaillons ensemble et la façon dont vos dispositifs sont conçus.",
  icon: ShieldCheck
}, {
  title: "Orientation résultats",
  description: "Les accompagnements sont conçus pour aboutir : durée d’enregistrement RNCP/RS la plus longue possible, absence de non-conformité à l’audit Qualiopi®. Ces résultats reposent sur une rigueur méthodologique et un engagement partagé entre Effet Conseil et vos équipes.",
  icon: Target
}];
function Reveal({
  children,
  className,
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} className={cn("transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8", className)} style={{
    transitionDelay: `${delay}ms`
  }}>
      {children}
    </div>;
}
export default function MonApproche() {
  return <section id="monapproche" className="bg-background relative overflow-hidden py-32 md:py-40">
      {}
      <div className="bg-primary/5 absolute top-0 left-0 h-full w-full -skew-y-3 transform" />

      <div className="container relative mx-auto px-4 md:px-6">
        <Reveal>
          <div className="mb-16 text-center md:mb-24">
            <h2 className="text-secondary font-serif text-4xl tracking-tight md:text-5xl lg:text-6xl">
              Mon approche
            </h2>
            <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-lg">Une approche pragmatique, éthique et orientée résultats.</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
          {pillars.map((pillar, index) => {
          const Icon = pillar.icon;
          let colClasses = "md:col-span-4";
          if (index === 3) {
            colClasses = "md:col-start-3 md:col-span-4";
          }
          return <Reveal key={pillar.title} delay={index * 100} className={cn("col-span-1", colClasses)}>
                <div data-index={index} className="h-full">
                  <Card className="bg-background/60 border-border/50 shadow-md hover:shadow-lg rounded-[var(--radius)] flex h-full flex-col backdrop-blur-md transition-all duration-300 hover:-translate-y-1">
                    <CardHeader className="pb-4">
                      <div className="bg-primary/10 text-primary mb-4 inline-flex size-12 items-center justify-center rounded-full">
                        <Icon className="size-6" />
                      </div>
                      <CardTitle className="font-serif text-2xl text-foreground">
                        {pillar.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-muted-foreground flex-grow leading-relaxed">
                      {pillar.description}
                    </CardContent>
                  </Card>
                </div>
              </Reveal>;
        })}
        </div>
      </div>
    </section>;
}